import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ReactComponent as RadioOn } from '../../../assets/images/svg/radio-on.svg';
import { ReactComponent as RadioOff } from '../../../assets/images/svg/radio-off.svg';
import { ReactComponent as InfoIcon } from '../../../assets/images/svg/info-filled-blue.svg';
import { pxToRem } from '../../../assets/styles/helper';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';
import Button from '../../Button/Button';
import ModalBody from '../../Modal/ModalBody';
import SectionLoader from '../../Loader/SectionLoader';
import { getDropoffLocations } from '../../../request/shippingRequest';
import { fetchAccountAddresses } from '../../../store/actions/account/accountActions';
import {
  getCurrentAccountUid,
  selectAccountAddresses,
  selectDefaultMailingAddress,
} from '../../../store/selectors/accountSelectors';
import addressTagType, {
  ADDRESS_TAG_TYPES,
  ADDRESS_TAG_TYPES_ICONS,
} from '../../../util/enum/api/addressTagType';
import { formatAddressStringInline } from '../../../util/helpers/addressHelper';
import useGetCountries from '../../../util/hooks/useGetCountries';
import { USA_COUNTRY_ISO3CODE } from '../../../util/constants';

const SELECT_ORIGIN = 'SELECT_ORIGIN';
const LIST = 'LIST';

const formatLocationAddress = (loc) => {
  const streetLines = Array.isArray(loc?.StreetLines)
    ? loc.StreetLines.filter(Boolean).join(', ')
    : '';
  return [
    streetLines,
    loc?.City,
    [loc?.StateOrProvinceCode, loc?.PostalCode].filter(Boolean).join(' '),
    loc?.CountryCode,
  ]
    .filter(Boolean)
    .join(', ');
};

// Label is "Home"/"Office" for those tags; anything else (Other tag, unknown,
// or untagged) falls back to "Other".
const getAddressLabel = (address) =>
  addressTagType[address?.TagType] || addressTagType[ADDRESS_TAG_TYPES.OTHER];

// Home/Office icon comes from the saved address tag; fall back to the generic
// location pin when an address has no tag so the row always shows an icon.
const getAddressIcon = (address) =>
  ADDRESS_TAG_TYPES_ICONS[address?.TagType] ||
  ADDRESS_TAG_TYPES_ICONS[ADDRESS_TAG_TYPES.OTHER];

const Title = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(18)};
  font-weight: 600;
  line-height: 1.4;
  color: ${themeColors.colorTextPrimary};
  padding-top: ${pxToRem(8)};
`;

const Body = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(16)};
  line-height: ${pxToRem(22)};
  color: ${themeColors.colorTextSecondary};
  margin-top: ${pxToRem(4)};
`;

const OriginBody = styled(Body)`
  line-height: ${pxToRem(18)};
`;

const AddressIconBox = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${pxToRem(40)};
  height: ${pxToRem(40)};
  border-radius: ${pxToRem(4)};
  background: ${themeColors.colorGrey};

  > svg {
    width: ${pxToRem(22)};
    height: ${pxToRem(22)};
  }
`;

const AddressTextCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(2)};
  min-width: 0;
`;

const AddressLabel = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(16)};
  font-weight: 600;
  line-height: ${pxToRem(18)};
  color: ${themeColors.colorTextPrimary};
`;

const AddressLineText = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(16)};
  line-height: ${pxToRem(18)};
  color: ${themeColors.colorTextSecondary};
`;

const SelectedAddressBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${pxToRem(12)};
  background: ${themeColors.colorBackgroundSecondary};
  border-radius: ${pxToRem(4)};
  padding: ${pxToRem(12)} ${pxToRem(16)};
  margin-top: ${pxToRem(16)};
`;

const NearbyCount = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(16)};
  line-height: ${pxToRem(22)};
  color: ${themeColors.colorTextSecondary};
  margin-top: ${pxToRem(16)};
`;

const OriginList = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  gap: ${pxToRem(12)};
  margin-top: ${pxToRem(16)};
  overflow-y: auto;
`;

const OriginCard = styled.button`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: ${pxToRem(12)};
  width: 100%;
  text-align: left;
  cursor: pointer;
  background: ${themeColors.colorBackgroundSecondary};
  border: 1px solid
    ${({ $selected }) =>
      $selected ? themeColors.colorTextSecondary : 'transparent'};
  border-radius: ${pxToRem(4)};
  padding: ${pxToRem(12)} ${pxToRem(16)};

  &:focus {
    outline: none;
  }
`;

const RadioIndicator = styled.span`
  display: flex;
  flex-shrink: 0;
  width: ${pxToRem(20)};
  height: ${pxToRem(20)};

  > svg {
    width: ${pxToRem(20)};
    height: ${pxToRem(20)};
  }

  ${({ $selected }) =>
    $selected &&
    `
    > svg circle:first-of-type {
      fill: ${themeColors.colorTextSecondary};
    }
    > svg circle:last-of-type {
      stroke: ${themeColors.colorTextSecondary};
    }
  `}
`;

const ResultsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(8)};
  margin-top: ${pxToRem(12)};
  overflow-y: auto;
`;

const LocationRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(2)};
  background: ${themeColors.colorBackgroundSecondary};
  border-radius: ${pxToRem(4)};
  padding: ${pxToRem(12)} ${pxToRem(16)};
`;

const LocationName = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(16)};
  font-weight: 600;
  line-height: ${pxToRem(18)};
  color: ${themeColors.colorTextPrimary};
`;

const LocationAddress = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(16)};
  line-height: ${pxToRem(18)};
  color: ${themeColors.colorTextSecondary};
`;

const EmptyState = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${pxToRem(12)};
  padding: 0 ${pxToRem(24)};
  text-align: center;

  > svg {
    width: ${pxToRem(36)};
    height: ${pxToRem(36)};
  }
`;

const EmptyText = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(18)};
  color: ${themeColors.colorTextSecondary};
  line-height: 1.5;
`;

const NeedHelpLink = styled.a`
  display: inline-block;
  margin-top: ${pxToRem(4)};
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  color: ${themeColors.colorPrimary};
  text-decoration: underline;
`;

const Divider = styled.hr`
  border: 0;
  border-top: 1px solid ${themeColors.colorBorderPrimary};
  margin: 0 ${pxToRem(-16)} ${pxToRem(16)};
`;

const Footer = styled.div`
  margin-top: auto;

  > button {
    width: 100%;
    font-family: ${variables.family.goldSilver}, sans-serif;
    font-size: ${pxToRem(16)};
    font-weight: 600;
    line-height: 1;
    color: ${themeColors.colorGrey};
  }

  > button:disabled,
  > button:disabled:hover {
    background: ${themeColors.colorGrey3};
    border-color: ${themeColors.colorGrey3};
    opacity: 1;
  }
`;

const SellFromHomeShipping = ({
  setSelectedOriginAddress,
  setBackInterceptor,
  handleNext,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const countries = useGetCountries();
  const accountUid = useSelector(getCurrentAccountUid);
  const accountAddresses = useSelector(selectAccountAddresses);
  const defaultMailingAddress = useSelector(selectDefaultMailingAddress);

  // SFH is available to verified accounts only, so there is always at least one
  // address on file. PO boxes can't be shipping origins, so exclude them.
  // SFH ships via FedEx Ship Centers in the US only, so origins are limited to
  // verified, non-PO-box, USA addresses.
  const verifiedAddresses = useMemo(
    () =>
      (accountAddresses || []).filter(
        (a) =>
          a?.IsVerified &&
          !a?.IsPoBox &&
          a?.CountryIso3Code === USA_COUNTRY_ISO3CODE,
      ),
    [accountAddresses],
  );
  const isMultiCase = verifiedAddresses.length > 1;
  const verifiedKey = verifiedAddresses.map((a) => a.AddressUid).join(',');

  const [subStep, setSubStep] = useState(LIST);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [locations, setLocations] = useState(null);
  const [isFetchingLocations, setIsFetchingLocations] = useState(false);
  const [locationsError, setLocationsError] = useState(false);

  useEffect(() => {
    if (accountUid && !accountAddresses?.length) {
      dispatch(fetchAccountAddresses({ accountUid }));
    }
  }, [accountUid, accountAddresses?.length, dispatch]);

  const fetchLocations = useCallback((address) => {
    if (!address?.AddressUid) return;
    setIsFetchingLocations(true);
    setLocationsError(false);
    getDropoffLocations({ addressUid: address.AddressUid })
      .then((response) => {
        setLocations(Array.isArray(response?.data) ? response.data : []);
      })
      .catch(() => {
        setLocations([]);
        setLocationsError(true);
      })
      .finally(() => setIsFetchingLocations(false));
  }, []);

  // Once addresses are available, pick the starting sub-step. Single verified
  // address -> straight to the drop-off list; multiple -> origin picker first.
  useEffect(() => {
    if (!accountAddresses?.length) return;
    if (isMultiCase) {
      setSubStep(SELECT_ORIGIN);
      setSelectedAddress(null);
      return;
    }
    const single =
      verifiedAddresses[0] || defaultMailingAddress || accountAddresses[0];
    setSubStep(LIST);
    setSelectedAddress(single);
    fetchLocations(single);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifiedKey, accountAddresses?.length]);

  // Let the wizard header back chevron return B2 -> B1 within this step before
  // leaving back to the Quantity step.
  useEffect(() => {
    if (typeof setBackInterceptor !== 'function') return undefined;
    setBackInterceptor(() => {
      if (isMultiCase && subStep === LIST) {
        setSubStep(SELECT_ORIGIN);
        return true;
      }
      return false;
    });
    return () => setBackInterceptor(null);
  }, [setBackInterceptor, isMultiCase, subStep]);

  const handleShowDropOff = () => {
    if (!selectedAddress) return;
    setSubStep(LIST);
    fetchLocations(selectedAddress);
  };

  const handleContinueToReview = () => {
    if (typeof setSelectedOriginAddress === 'function') {
      setSelectedOriginAddress(selectedAddress);
    }
    if (typeof handleNext === 'function') handleNext();
  };

  const supportPhone = t('sellWizard.sfhWizard.phone');
  const addressesReady = Boolean(accountAddresses?.length);

  return (
    <SectionLoader isLoading={isFetchingLocations || !addressesReady}>
      <ModalBody>
        {addressesReady && subStep === SELECT_ORIGIN && (
          <>
            <Title>
              {t('sellWizard.sfhWizard.shipping.selectOriginTitle')}
            </Title>
            <OriginBody>
              {t('sellWizard.sfhWizard.shipping.selectOriginSubtext')}
            </OriginBody>
            <OriginList data-cy="list-sfh-origin-addresses">
              {verifiedAddresses.map((address) => {
                const isSelected =
                  selectedAddress?.AddressUid === address.AddressUid;
                return (
                  <OriginCard
                    type="button"
                    key={address.AddressUid}
                    $selected={isSelected}
                    onClick={() => setSelectedAddress(address)}
                    data-cy={`radio-sfh-origin-${address.AddressUid}`}
                  >
                    <RadioIndicator $selected={isSelected}>
                      {isSelected ? <RadioOn /> : <RadioOff />}
                    </RadioIndicator>
                    <AddressIconBox>{getAddressIcon(address)}</AddressIconBox>
                    <AddressTextCol>
                      {getAddressLabel(address) && (
                        <AddressLabel>{getAddressLabel(address)}</AddressLabel>
                      )}
                      <AddressLineText>
                        {formatAddressStringInline(address, countries)}
                      </AddressLineText>
                    </AddressTextCol>
                  </OriginCard>
                );
              })}
            </OriginList>
            <Footer>
              <Divider />
              <Button
                variant="primary"
                textTransform="none"
                disabled={!selectedAddress}
                onClick={handleShowDropOff}
                data-cy="button-sfh-show-dropoff"
              >
                {t('sellWizard.sfhWizard.shipping.showDropOffLocations')}
              </Button>
            </Footer>
          </>
        )}

        {addressesReady && subStep === LIST && (
          <>
            <Title>{t('sellWizard.sfhWizard.shipping.title')}</Title>
            <Body>{t('sellWizard.sfhWizard.shipping.body')}</Body>

            <SelectedAddressBox data-cy="box-sfh-origin-address">
              <AddressIconBox>{getAddressIcon(selectedAddress)}</AddressIconBox>
              <AddressTextCol>
                {getAddressLabel(selectedAddress) && (
                  <AddressLabel>
                    {getAddressLabel(selectedAddress)}
                  </AddressLabel>
                )}
                <AddressLineText>
                  {formatAddressStringInline(selectedAddress, countries)}
                </AddressLineText>
              </AddressTextCol>
            </SelectedAddressBox>

            {locations !== null && !locationsError && locations.length > 0 && (
              <NearbyCount data-cy="text-sfh-locations-count">
                {t('sellWizard.sfhWizard.shipping.locationsNearby', {
                  count: locations.length,
                })}
              </NearbyCount>
            )}

            {locations !== null &&
              (locations.length === 0 ? (
                <EmptyState data-cy="text-sfh-no-locations">
                  <InfoIcon />
                  <EmptyText>
                    {t('sellWizard.sfhWizard.shipping.noResultsTitle')}
                  </EmptyText>
                  <NeedHelpLink
                    href={`tel:${supportPhone.replace(/[^\d+]/g, '')}`}
                  >
                    {t('sellWizard.sfhWizard.needHelp')} {supportPhone}
                  </NeedHelpLink>
                </EmptyState>
              ) : (
                <ResultsList data-cy="list-sfh-locations">
                  {locations.map((location, idx) => (
                    <LocationRow
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${location.CompanyName}-${idx}`}
                    >
                      <LocationName>{location.CompanyName}</LocationName>
                      <LocationAddress>
                        {formatLocationAddress(location)}
                      </LocationAddress>
                    </LocationRow>
                  ))}
                </ResultsList>
              ))}

            <Footer>
              <Divider />
              <Button
                variant="primary"
                textTransform="none"
                onClick={handleContinueToReview}
                data-cy="button-sfh-continue-review"
              >
                {t('sellWizard.sfhWizard.shipping.continueToReview')}
              </Button>
            </Footer>
          </>
        )}
      </ModalBody>
    </SectionLoader>
  );
};

SellFromHomeShipping.propTypes = {
  setSelectedOriginAddress: PropTypes.func,
  setBackInterceptor: PropTypes.func,
  handleNext: PropTypes.func,
};

export default SellFromHomeShipping;
